import React, {
    useCallback,
    useEffect,
    useEffectEvent,
    useMemo,
    useState,
} from "react";
import {
    DateOnlyDateRange,
    toDateOnlyDateRange,
    toLocalDateRange,
} from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import { isEqual } from "@common/helpers/comparison";
import { omitBy } from "@common/helpers/objects";
import {
    HistoryFilter,
    HistoryItem,
    ResourceType,
} from "@common/types/auditLog";
import { CalendarIcon } from "@icons";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import { removeTimeZoneOffset, SUGGESTED_PRESETS } from "@helpers/date";
import { Loader } from "@design-system/Loader/Loader";
import { Pagination } from "@design-system/Pagination/Pagination";
import { HStack } from "@design-system/HStack/HStack";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { pluralizeWithCount } from "@common/helpers/string";
import { HistoryField } from "@common/constants/history-source-types";
import { useGetHistoryQuery } from "@api/auditLog.api";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { useActiveWorkspaceQuery } from "@hooks/useActiveWorkspace";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import {
    useHistoryPageFiltersCache,
    HistoryFilters,
    RevertedFilterValue,
} from "@hooks/view/useHistoryPageFiltersCache";
import { HistoryRow } from "./views/historyViewConfig";
import { HistoryDrawerContextProvider } from "./HistoryDrawerContextProvider";
import { useIsChannelAttributionEnabled } from "./hooks/useIsChannelAttributionEnabled";
import {
    buildActivityFilters,
    excludeDisabledActivityValues,
    excludeDisabledChannels,
    getActivitiesFromValues,
} from "./components/historyActivityFilters";
import styles from "./HistoryPage.module.scss";
import { History } from "./History";
import { HistoryHeatmap } from "./HistoryHeatmap";
import { HistoryLegacyToolbar } from "./HistoryLegacyToolbar";
import { HistoryTimeline } from "./timeline/HistoryTimeline";
import { HistoryTimelineToolbar } from "./timeline/HistoryTimelineToolbar";
import { useHistoryPageTimeline } from "./timeline/useHistoryPageTimeline";
import { mergeHistoryRowsByAgent } from "./utils/mergeHistoryRowsByAgent";
import { getHistoryRowId, historyItemToRow } from "./utils/historyItemToRow";

const DEFAULT_FILTERS: HistoryFilters = {
    entityIds: [],
    processSourceTypes: [],
    processFields: [],
};

/**
 * A history item represents a category change when it is a category-entity
 * activity or a transaction update whose only grouped field is the category.
 * GL-first workspaces hide these because they do not classify by category.
 */
function isCategoryHistoryItem(item: HistoryItem): boolean {
    if (item.resourceType === ResourceType.CATEGORY) {
        return true;
    }
    return (
        item.fields.length > 0 &&
        item.fields.every((field) => field === HistoryField.CATEGORY_ID)
    );
}

function sanitizeHistoryFilters(filters: HistoryFilters): HistoryFilters {
    const activityValues = filters.activityValues?.map((value) => {
        if (value === "account") {
            return "financial-account";
        }
        if (value === "cash-ledger") {
            return "general-ledger";
        }
        return value;
    });

    const sanitized: HistoryFilters = {
        ...filters,
        activityValues,
    };

    if (!sanitized.activityValues?.length) {
        delete sanitized.activityValues;
        delete sanitized.activityFilters;
        delete sanitized.resourceTypes;
        delete sanitized.fields;
    }

    return sanitized;
}

const PAGE_SIZE = 100;

const HistoryPageContent = ({
    historyStartAt,
}: {
    historyStartAt: Date | undefined;
}) => {
    const [cachedFilters, setCachedFilters] = useHistoryPageFiltersCache();
    const [filters, setFilters] = useState<HistoryFilters>(() =>
        sanitizeHistoryFilters({
            ...DEFAULT_FILTERS,
            ...cachedFilters,
        }),
    );

    const cleanUpStaleCachedFilters = useEffectEvent(() => {
        const rawCached = cachedFilters ?? {};
        const sanitized = sanitizeHistoryFilters({
            ...DEFAULT_FILTERS,
            ...rawCached,
        });
        if (!isEqual(sanitized, { ...DEFAULT_FILTERS, ...rawCached })) {
            setCachedFilters(sanitized);
        }
    });
    useEffect(() => {
        cleanUpStaleCachedFilters();
    }, []);
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState<string>();
    const { data: members } = useCurrentWorkspaceMembers();
    const {
        activityHeatmap: isActivityHeatmapEnabled,
        schedules: isSchedulesEnabled,
        agentRuns: isAgentRunsEnabled,
    } = useTypedFlags();
    const isChannelFilterEnabled = useIsChannelAttributionEnabled();
    const { enabled: isGlFirst } = useIsGlFirst();
    const dateRange = useMemo(
        () => toDateOnlyDateRange({ start: filters.start, end: filters.end }),
        [filters.start, filters.end],
    );
    const apiFilters = useMemo<HistoryFilter>(() => {
        const result: HistoryFilter = {};

        if (filters.entityIds && filters.entityIds.length > 0) {
            result.entityIds = filters.entityIds;
        }

        if (filters.actorIds && filters.actorIds.length > 0) {
            result.actorIds = filters.actorIds;
        }

        if (filters.actorType) {
            result.actorType = filters.actorType;
        }

        if (filters.changeTypes && filters.changeTypes.length > 0) {
            result.changeTypes = filters.changeTypes;
        }

        if (filters.activityValues?.length) {
            const enabledActivityValues = excludeDisabledActivityValues(
                filters.activityValues,
                { isSchedulesEnabled },
            );
            const activityFilters = buildActivityFilters(
                getActivitiesFromValues(enabledActivityValues),
                filters.changeTypes,
            );
            if (activityFilters?.length) {
                result.activityFilters = activityFilters;
            }
        }

        const combinedSourceTypes = [...(filters.sourceTypes ?? [])];
        if (combinedSourceTypes.length > 0) {
            result.sourceTypes = combinedSourceTypes;
        }

        if (filters.sourcePredicates && filters.sourcePredicates.length > 0) {
            result.sourcePredicates = filters.sourcePredicates;
        }

        if (filters.start) {
            result.minCreatedAt = removeTimeZoneOffset(filters.start);
        }

        if (filters.end) {
            result.maxCreatedAt = removeTimeZoneOffset(filters.end);
        }

        const hasActionSelection =
            (filters.changeTypes?.length ?? 0) > 0 ||
            filters.revertedFilter !== undefined;

        if (filters.revertedFilter === RevertedFilterValue.REVERTED) {
            result.revertedOnly = true;
        } else if (
            filters.revertedFilter === RevertedFilterValue.ALL ||
            !hasActionSelection
        ) {
            result.showReverted = true;
        }

        if (isChannelFilterEnabled) {
            const enabledChannels = excludeDisabledChannels(filters.channels, {
                isAgentRunsEnabled,
            });
            if (enabledChannels.length > 0) {
                result.channels = enabledChannels;
            }
        }

        if (search) {
            result.search = search;
        }

        return result;
    }, [
        filters,
        search,
        isSchedulesEnabled,
        isChannelFilterEnabled,
        isAgentRunsEnabled,
    ]);

    const {
        isActivityTimelineEnabled,
        timeline,
        listFilter,
        hasActiveTimelineFilters,
        previewWindow,
        setPreviewWindow,
        chartDensity,
    } = useHistoryPageTimeline({
        dateRange,
        historyStartAt,
        apiFilters,
        outcome: filters.outcome,
        series: filters.series,
    });

    const { data: response, isLoading } = useGetHistoryQuery({
        filters: listFilter,
        page: currentPage,
        limit: PAGE_SIZE,
        groupByChannel: isChannelFilterEnabled ? true : undefined,
    });

    const totalPages = response?.totalPages ?? 1;
    const totalLogs = response?.total ?? 0;

    const handleFiltersChange = useCallback(
        (value: Partial<HistoryFilters>) => {
            const newFilters = sanitizeHistoryFilters(
                omitBy(
                    { ...filters, ...value },
                    (v) => v === null || v === undefined,
                ),
            );

            if (isEqual(newFilters, filters)) {
                return;
            }

            setFilters(newFilters);
            setCachedFilters(newFilters);
            setCurrentPage(1);
        },
        [filters, setCachedFilters],
    );

    const handlePageChange = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);

    const handleDateRangeChange = useCallback(
        (range: DateOnlyDateRange) => {
            const localRange = toLocalDateRange(range);
            handleFiltersChange({
                start: localRange.start,
                end: localRange.end,
            });
        },
        [handleFiltersChange],
    );

    const handleSearchChange = useCallback((value: string | undefined) => {
        setSearch(value);
        setCurrentPage(1);
    }, []);

    const handleClearFilters = useCallback(() => {
        const clearedFilters: HistoryFilters = isActivityTimelineEnabled
            ? {
                  ...DEFAULT_FILTERS,
                  ...(filters.start && { start: filters.start }),
                  ...(filters.end && { end: filters.end }),
              }
            : DEFAULT_FILTERS;
        setFilters(clearedFilters);
        setCachedFilters(clearedFilters);
        setSearch(undefined);
        setCurrentPage(1);
    }, [
        isActivityTimelineEnabled,
        filters.start,
        filters.end,
        setCachedFilters,
    ]);

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_HISTORY_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    const hasActiveFilters = useMemo(
        () =>
            (filters.entityIds?.length ?? 0) > 0 ||
            (filters.actorIds?.length ?? 0) > 0 ||
            filters.actorType !== undefined ||
            (filters.sourceTypes?.length ?? 0) > 0 ||
            (filters.sourcePredicates?.length ?? 0) > 0 ||
            (filters.changeTypes?.length ?? 0) > 0 ||
            filters.revertedFilter !== undefined ||
            (filters.activityValues?.length ?? 0) > 0 ||
            (filters.resourceTypes?.length ?? 0) > 0 ||
            (isChannelFilterEnabled && (filters.channels?.length ?? 0) > 0) ||
            (!isActivityTimelineEnabled &&
                (filters.start !== undefined || filters.end !== undefined)) ||
            search !== undefined ||
            hasActiveTimelineFilters,
        [
            filters,
            search,
            isChannelFilterEnabled,
            isActivityTimelineEnabled,
            hasActiveTimelineFilters,
        ],
    );

    const historyData = useMemo<HistoryRow[]>(() => {
        const historyItems = response?.data ?? [];
        const filteredHistoryItems = historyItems.filter(
            (item: HistoryItem) => !(isGlFirst && isCategoryHistoryItem(item)),
        );

        const rows = filteredHistoryItems.map((item: HistoryItem) => {
            const member = item.actorId
                ? members?.find((m) => m.user.id === item.actorId)
                : undefined;
            return {
                ...historyItemToRow({ item, id: getHistoryRowId(item) }),
                member,
            };
        });

        return mergeHistoryRowsByAgent(rows);
    }, [response?.data, members, isGlFirst]);

    const dateFilter = (
        <DateRangeFilterNext
            dateRange={dateRange}
            onChange={handleDateRangeChange}
            pinnedPresets={pinnedPresets}
            onTogglePin={onTogglePin}
            previewRange={previewWindow}
            {...(isActivityTimelineEnabled && {
                leftIcon: <CalendarIcon />,
                ...timeline.dateFilterProps,
            })}
        />
    );

    const footer = (
        <CommandIsland
            sticky
            className={commandIslandStyles.bulkActionsStickyFooter}
        >
            <HStack gap={8}>
                {totalPages > 1 && (
                    <Pagination
                        currentPage={currentPage}
                        pageCount={totalPages}
                        onPageChange={handlePageChange}
                        size="md"
                        buttonsPosition="left"
                    />
                )}
                <CommandIslandTotal>
                    {pluralizeWithCount("activity", totalLogs)}
                </CommandIslandTotal>
            </HStack>
        </CommandIsland>
    );

    const renderContent = () => {
        if (isLoading) {
            return <Loader />;
        }

        return (
            <History
                data={historyData}
                footer={footer}
                clearFilters={hasActiveFilters ? handleClearFilters : undefined}
                className={
                    isActivityTimelineEnabled ? styles.timelineTable : undefined
                }
            />
        );
    };

    return (
        <HistoryDrawerContextProvider
            revertedFilter={filters.revertedFilter}
            channels={isChannelFilterEnabled ? apiFilters.channels : undefined}
            outcome={listFilter.outcome}
            isGroupedByChannel={isChannelFilterEnabled}
        >
            <main className={styles.container}>
                {isActivityTimelineEnabled ? (
                    <HistoryTimelineToolbar
                        filters={filters}
                        onFiltersChange={handleFiltersChange}
                        search={search}
                        onSearchChange={handleSearchChange}
                        isChannelFilterEnabled={isChannelFilterEnabled}
                        onClearFilters={
                            hasActiveFilters ? handleClearFilters : undefined
                        }
                        chartDensity={chartDensity}
                        dateFilter={dateFilter}
                    />
                ) : (
                    <HistoryLegacyToolbar
                        filters={filters}
                        onFiltersChange={handleFiltersChange}
                        defaultFilters={DEFAULT_FILTERS}
                        isChannelFilterEnabled={isChannelFilterEnabled}
                        onClearFilters={
                            hasActiveFilters ? handleClearFilters : undefined
                        }
                        dateFilter={dateFilter}
                    />
                )}

                {isActivityTimelineEnabled ? (
                    <HistoryTimeline
                        filter={listFilter}
                        range={timeline.range}
                        previewWindow={previewWindow}
                        today={timeline.today}
                        chartDensity={chartDensity}
                        onWindowChange={handleDateRangeChange}
                        onWindowPreview={setPreviewWindow}
                        onFiltersChange={handleFiltersChange}
                    />
                ) : (
                    isActivityHeatmapEnabled && (
                        <HistoryHeatmap
                            filters={apiFilters}
                            onDateSelect={(date) =>
                                handleDateRangeChange({
                                    start: date,
                                    end: date,
                                })
                            }
                        />
                    )
                )}
                {renderContent()}
            </main>
        </HistoryDrawerContextProvider>
    );
};

/**
 * With the timeline on, waits for the workspace so the first chart and list
 * already fit its creation instead of refetching once it loads. If the
 * workspace fails to load, the page falls back to the default range.
 */
export const HistoryPage = () => {
    const { activityTimeline: isActivityTimelineEnabled } = useTypedFlags();
    const { workspace, isLoading } = useActiveWorkspaceQuery();

    if (isActivityTimelineEnabled && isLoading) {
        return <Loader />;
    }
    return (
        <HistoryPageContent
            historyStartAt={
                workspace ? new Date(workspace.createdAt) : undefined
            }
        />
    );
};
